import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { RxState } from '@rx-angular/state';
import { IAuthState, IChangePasswordDTO, IError, ILoginDTO, IOnlyEmailDTO, IRefreshTokenDTO, IRegisterDTO, IUser, ILoginSocialDTO, Language } from 'common_library';
import { JwtHelperService } from '@auth0/angular-jwt';
import { interval, merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import { _ } from '../../consts';
import { GLOBAL_RX_STATE, GlobalState } from '../../app-state.tokens';
import { StoreService } from '../utils/store.service';
import { HttpIoService } from '../communication/http-io.service';
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication, SignInResult } from '@capacitor-firebase/authentication';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { MainState } from 'src/app/types/state/app-main-state.interface';
import { MainStateService } from '../state/app-main-state.service';
import { ToastService } from '../utils/toast.service';

export interface IAppleUser {
  email?: string,
  identityToken?: string,
  familyName?: string,
  user?: string,
  givenName?: string,
  authorizationCode?: string
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  _authState: IAuthState;
  private lastRefreshErrorToastAt = 0;
  private readonly refreshErrorToastCooldownMs = 60 * 1000;

  constructor(
    @Inject(GLOBAL_RX_STATE) private globalState: RxState<GlobalState>,
    private store: StoreService,
    private HIO: HttpIoService,
    public modalCtrl: ModalController,
    private mainStateService: MainStateService, 
    private toast: ToastService,
    private translate: TranslateService
  ) {
    this._authState = this.store.get<IAuthState>(_.AUTHSTATE_KEY);
    if (this._authState?.user) {
      this.mainStateService.setUser(this._authState.user);
    }
    const backVisibleEvent$ = this.globalState.select("visible").pipe(filter(v => !!v));
    const backOnlineEvent$ = this.globalState.select("mainState").pipe(filter(ms => ms === MainState.ONLINE));
    const needToRefreshToken$ = merge(backVisibleEvent$, backOnlineEvent$, interval(10 * 60 * 1000)); // quando torno visibile o online oppure ogni 10 minuti
    needToRefreshToken$.subscribe(this.refresh.bind(this));
  }

  get authState(): IAuthState {
    return this._authState;
  }

  set authState(value: IAuthState) {
    this._authState = value;
    if (!!value) {
      this.store.set(_.AUTHSTATE_KEY, value);
      this.mainStateService.setUser(value.user);
    }
    else {
      this.store.remove(_.AUTHSTATE_KEY);
      this.mainStateService.setUser(null);
    }
  }

  get isAuthenticated(): boolean {
    return !!this.authState ? !!this.authState.user : false;
  }

  async refresh() {
    if (!!this.authState) {
      if (!this.authState.refreshToken) {
        console.warn('Auth state has no refresh token. Skipping automatic refresh.');
        return;
      }
      try {
        console.log("REFRESH TOKEN", this.authState.refreshToken.substring(this.authState.refreshToken.length - 4));
        //TODO: AGGIUNGERE USER AGENT O VALORE ENUM
        const platformType = this.mainStateService.getPlatformType();
        const dto: IRefreshTokenDTO = { refreshToken: this.authState.refreshToken, clientId: this.mainStateService.getClientId(), userAgent: navigator.userAgent, platformType };
        const rv = await this.HIO.post<IAuthState | IError, IRefreshTokenDTO>('auth/refresh', dto);
        if (!rv) {
          console.log("REFRESH TOKEN EMPTY RESPONSE");
          this.showRefreshServerBusyToast();
          return;
        }
        if ('code' in rv) {
          console.log("REFRESH TOKEN FAILED", rv);
          if (this.shouldLogoutFromRefreshError(rv)) {
            this.authState = null;
          } else {
            this.showRefreshServerBusyToast();
          }
        } else {
          console.log("REFRESH TOKEN SUCCESS", rv.refreshToken.substring(rv.refreshToken.length - 4));
          this.authState = rv;
        }
      }
      catch (err) {
        console.log('refresh error', err);
        if (this.isUnauthorizedHttpError(err)) {
          this.authState = null;
        } else {
          this.showRefreshServerBusyToast();
        }
      }
    }
  }

  async login(email: string, password: string): Promise<IUser> {
    try {
      const platformType = this.mainStateService.getPlatformType();
      const dto: ILoginDTO = { email, password, clientId: this.mainStateService.getClientId(), userAgent: navigator.userAgent, platformType };
      const rv = await this.HIO.post<IAuthState | IError, ILoginDTO>('auth/login', dto);
      if (!rv || ('code' in rv)) {
        return null;
      }
      this.authState = rv;
      return rv?.user;
    } catch (error) {
      console.log('login error', error);
    }
  }

  private isUnauthorizedHttpError(err: unknown): boolean {
    const httpErr = err as HttpErrorResponse;
    return httpErr?.status === 401 || httpErr?.status === 403;
  }

  private shouldLogoutFromRefreshError(err: IError): boolean {
    const code = (err as IError)?.code;
    if (code === 1 || code === 2 || code === 3 || code === 4) return true;
    const message = (err as IError)?.message;
    if (typeof message === 'string') {
      const lower = message.toLowerCase();
      if (lower.includes('jwt') || lower.includes('token non valido') || lower.includes('token revocato')) {
        return true;
      }
    }
    return false;
  }

  private showRefreshServerBusyToast(): void {
    const now = Date.now();
    if (now - this.lastRefreshErrorToastAt < this.refreshErrorToastCooldownMs) return;
    this.lastRefreshErrorToastAt = now;
    this.toast.presentBasicToast({
      header: this.translate.instant('TOAST_MESSAGES.WARNING'),
      message: this.translate.instant('TOAST_MESSAGES.SERVER_BUSY'),
      color: 'warning',
      icon: 'warning'
    });
  }

  // per ora il silentLogout è identico al logout
  async silentLogoutIfLogged() {
    await this.logout();
  }

  async logout(): Promise<void> {
    try {
      if (!!this.authState) {
        await this.HIO.get<boolean>('auth/logout');
        this.authState = null;
      }
    } catch (err) {
      console.log('logout error', err);
    }
  }

  // ===== Impersonation (solo admin — assistenza) =============================
  private jwtHelper = new JwtHelperService();

  /** Ricerca utenti da impersonare (endpoint admin-only lato backend). */
  async searchUsers(term: string): Promise<IUser[]> {
    try {
      return (await this.HIO.get<IUser[]>(`user/admin/search/${encodeURIComponent(term)}`)) ?? [];
    } catch (err) {
      console.log('searchUsers error', err);
      return [];
    }
  }

  /** Chiede al backend un token per l'utente `impersonateId` (guardia admin lato server). */
  async impersonate(impersonateId: string): Promise<IAuthState | null> {
    try {
      return await this.HIO.post<IAuthState, { impersonateId: string }>('auth/impersonate', { impersonateId });
    } catch (err) {
      console.log('impersonate error', err);
      return null;
    }
  }

  get isImpersonating(): boolean {
    return !!this.store.get(_.IMPERSONATOR_KEY);
  }

  /** Utente attualmente simulato (per il banner). Null se non stai impersonando. */
  get impersonatedUser(): IUser | null {
    return this.isImpersonating ? (this._authState?.user ?? null) : null;
  }

  /**
   * Entra in impersonation: mette da parte stato + clientId (device) dell'admin,
   * allinea CLIENT_KEY al client DEDICATO del token (decodificato) e passa all'utente
   * simulato. Le notifiche: il backend ha già spostato i device FCM dell'admin
   * sull'utente simulato (per il clientId admin); l'app NON registra nuovi token durante
   * l'impersonation (vedi push service, gate su isImpersonating).
   * NB: CLIENT_KEY va scritto RAW (la factory in app.module rigenera se inizia con '"').
   */
  async enterImpersonation(impersonatedState: IAuthState): Promise<void> {
    this.store.set(_.IMPERSONATOR_KEY, { state: this._authState, clientId: this.mainStateService.getClientId() });
    const payload = this.jwtHelper.decodeToken(impersonatedState.accessToken);
    if (payload?.clientId) localStorage.setItem(_.CLIENT_KEY, payload.clientId);
    this.authState = impersonatedState;
    window.location.reload();
  }

  /**
   * Torna all'account admin: chiama stop-impersonate (col token impersonation ancora
   * attivo) → il backend ridà i device all'admin e chiude il client dedicato. Poi
   * ripristina stato + clientId del device admin (mai toccato) e ricarica.
   */
  async exitImpersonation(): Promise<void> {
    const saved = this.store.get<{ state: IAuthState; clientId: string }>(_.IMPERSONATOR_KEY);
    if (!saved) return;
    try {
      await this.HIO.post('auth/stop-impersonate', { clientId: saved.clientId });
    } catch (e) {
      console.log('stop-impersonate error', e);
    }
    this.store.remove(_.IMPERSONATOR_KEY);
    if (saved.clientId) localStorage.setItem(_.CLIENT_KEY, saved.clientId);
    this.authState = saved.state;
    window.location.reload();
  }

  async register(
    email: string,
    password: string,
    name: string,
    surname: string,
    lang: Language,
    consents?: Pick<IRegisterDTO, 'contractAccepted' | 'marketingPushConsent' | 'marketingEmailConsent'>
  ): Promise<boolean | IError> {
    try {
      const dto: IRegisterDTO = {
        email,
        password,
        name,
        surname,
        lang,
        contractAccepted: consents?.contractAccepted ?? false,
        marketingPushConsent: consents?.marketingPushConsent ?? false,
        marketingEmailConsent: consents?.marketingEmailConsent ?? false,
      };
      return await this.HIO.post<boolean, IRegisterDTO>(`auth/register`, dto);
    } catch (err) {
      console.log('register error', err);
    }
  }

  async forgotPassword(email: string): Promise<boolean> {
    try {
      const dto: IOnlyEmailDTO = { email };
      return await this.HIO.post<boolean, IOnlyEmailDTO>(`auth/forgotPw`, dto);
    } catch (err) {
      console.log('forgotPassword error', err);
    }
  }

  async changePassword(email: string, password: string): Promise<boolean> {
    try {
      const dto: IChangePasswordDTO = { email, password };
      return await this.HIO.post<boolean, IChangePasswordDTO>(`auth/changePw`, dto);
    } catch (error) {
      console.log('changePassword error', error);
    }
  }

  async openGoogleSignIn(): Promise<IUser | IError> {
    const platformType = this.mainStateService.getPlatformType();
    if (Capacitor.getPlatform() === 'web') {
      //Se l'integrazione è sufficiente, si può asciare tutto insieme
      try {
        const result: SignInResult = await FirebaseAuthentication.signInWithGoogle();

        if (!result) return { code: 10, message: 'Login non riuscito' }

        const dto: ILoginSocialDTO = {
          clientId: this.mainStateService.getClientId(),
          userAgent: navigator.userAgent,
          signInResponse: result,
          platformType
        }

        const rv: IAuthState | IError = await this.HIO.post<any, any>(`firebase/google-login/`, dto);

        if ('code' in rv) return rv;
        this.authState = rv;

        return this.authState?.user;
      } catch (err) {
        if (err.code === 'auth/popup-closed-by-user') return { code: 10, message: 'Procedura di login annullata' }
        return { code: 10, message: 'Login non riuscito' }
      }
    } else {
      try {
        const result: SignInResult = await FirebaseAuthentication.signInWithGoogle();

        if (!result) return { code: 10, message: 'Login non riuscito' }

        const dto: ILoginSocialDTO = {
          clientId: this.mainStateService.getClientId(),
          userAgent: navigator.userAgent,
          signInResponse: result,
          platformType
        }

        const rv: IAuthState | IError = await this.HIO.post<any, any>(`firebase/google-login/`, dto);

        if ('code' in rv) return rv;
        this.authState = rv;

        return this.authState?.user;
      } catch (err) {
        if (err.code === 'auth/popup-closed-by-user') return { code: 10, message: 'Procedura di login annullata' }
        return { code: 10, message: 'Login non riuscito' }
      }
    }
  }

  async openAppleSignIn(): Promise<IUser | IError> {
    const platformType = this.mainStateService.getPlatformType();
    if (Capacitor.getPlatform() === 'web') {
      try {
        const result: SignInResult = await FirebaseAuthentication.signInWithApple({
          scopes: [
            "email",
            "name",
          ],
          customParameters: [
            {
              key: "locale",
              value: "it_IT"
            }
          ]
        });
        if (!result) {
          return { code: 10, message: 'Login non riuscito' }
        }
        const dto: ILoginSocialDTO = {
          clientId: this.mainStateService.getClientId(),
          userAgent: navigator.userAgent,
          signInResponse: result,
          platformType
        }
        const rv: IAuthState | IError = await this.HIO.post<any, any>(`firebase/apple-login/`, dto);

        if ('code' in rv) return rv;
        this.authState = rv;

        return this.authState?.user;
      } catch (err) {

        if (err.code === 'auth/popup-closed-by-user') return { code: 10, message: 'Procedura di login annullata' }
        return { code: 10, message: 'Login non riuscito' }
      }
    } else {
      try {
        const result: SignInResult = await FirebaseAuthentication.signInWithApple({
          scopes: [
            "email",
            "name",
          ],
        });
        if (!result) return { code: 10, message: 'Login non riuscito' }

        const dto: ILoginSocialDTO = {
          clientId: this.mainStateService.getClientId(),
          userAgent: navigator.userAgent,
          signInResponse: result,
          platformType
        }

        const rv: IAuthState | IError = await this.HIO.post<any, any>(`firebase/apple-login/`, dto);

        if ('code' in rv) return rv;
        this.authState = rv;

        return this.authState?.user;
      } catch (err) {

        if (err.code === 'auth/popup-closed-by-user') return { code: 10, message: 'Procedura di login annullata' }
        return { code: 10, message: 'Login non riuscito' }
      }
    }
  }

  // async checkAuthAndPlanRefresh() {
  //   //console.log('checkAuthAndPlanRefresh');
  //   if (!this.state) {
  //     return;
  //   }

  //   const accessExpired = this.jwtHelper.isTokenExpired(this.state.accessToken);
  //   if (accessExpired) {
  //     return this.refreshAuth();
  //   }

  //   this.planRefresh(this.state.accessToken);
  //   this.user$.next(this.state.user);
  // }

  // async refreshAuth() {
  //   if (this.state) {
  //     // Timeout necessario altrimenti -> Error: providers are not ready yet
  //     /*if (this.state.isSocial) this.refreshSocial();
  //     else*/   // --ipts
  //     await this.refreshStandard(this.state.refreshToken);
  //   }
  // }

  // private planRefresh(accessToken: string) {
  //   const timeoutEdge = 60000; // 10 minuti
  //   const tokenExpirationDate: Date = this.jwtHelper.getTokenExpirationDate(accessToken);
  //   const tokenExpTimestamp = tokenExpirationDate.getTime();
  //   const nowTimestamp = new Date().getTime();

  //   let timeout = tokenExpTimestamp - nowTimestamp; /* - 60 * 1000 */
  //   // console.log('🐱️ : timeout in minuti', timeout / 60000);
  //   if(timeout<=timeoutEdge){
  //     timeout = 100
  //   }else{
  //     timeout = timeoutEdge
  //   }
  //   if (this.refreshTimer) {
  //     clearTimeout(this.refreshTimer);
  //   }

  //   this.refreshTimer = setTimeout(() => {
  //     this.refreshAuth();
  //   }, timeout);
  // }

  // private stopRefreshTokenTimer() {
  //   clearTimeout(this.refreshTimer);
  // }



  // async refreshStandard(refreshToken: string): Promise<void> {
  //   console.log('🐱️ : refresh STANDARD - START');
  //   try {
  //     const dto: IRefreshTokenDTO = { refreshToken, clientId:this.app.clientId, userAgent:navigator.userAgent  };
  //     const rv = await lastValueFrom(this.http.post<IAuthState | number>(`${this.app.apiUrl}/auth/refresh`, dto));

  //     if(typeof rv === "number") {
  //       console.log("RefreshToken Return Value",rv);
  //       this.logoutAndClearState();
  //     } else {
  //       this.updateState(rv);
  //     }

  //   } catch (error) {
  //     console.log('🐱️ : error', error);
  //     // if (error.status === 401) {
  //     //   this.logoutAndClearState();
  //     // }
  //     // this.throwError(error);
  //   }
  //   console.log('🐱️ : refresh STANDARD - END');
  // }
  /*
  async loginSocial() {
    // console.log('🐱️ : login SOCIAL');
    try {
      const socialUser = await this.socialAuth.signIn(GoogleLoginProvider.PROVIDER_ID);
      return socialUser.email;
    } catch (error) {
      console.log('🐱️ : error', error);
      this.throwError(error);
    }
  }
  */
  /*
  async refreshSocial() {
    // console.log('🐱️ : refresh SOCIAL');
    try {
      await this.loading.presentLoading({ message: 'Login in corso' });
      setTimeout(() => this.socialAuth.refreshAuthToken(GoogleLoginProvider.PROVIDER_ID), 2000);
    } catch (error) {
      console.log('🐱️ : error', error);
      this.logoutAndClearState();
      this.throwError(error);
    }
  }
  */
  /*
  async updateSocial(socialUser: SocialUser): Promise<IUser> {
    // console.log('🐱️ : updateSocial');
    try {
      const dto: ISocialLoginDTO = {
        token: socialUser.idToken,
        social: socialUser.provider,
        clientId: this.app.clientId,
      };
      const state = await this.http
        .post<IAuthState>(`${this.app.apiUrl}/auth/social-login`, dto)
        .toPromise();
      this.updateState(state);
      return this.state.user;
    } catch (error) {
      console.log('🐱️ : error', error);
      this.throwError(error);
    }
  }
  */

}
